import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { fetchKanaOrigins } from "@/lib/api";

// Hiragana U+3041-U+3096 + Katakana U+30A1-U+30FA. The kanji data
// stores on'yomi as EITHER (older entries use katakana, newer use
// hiragana). The kana → origin map is keyed by katakana UNVOICED, so
// we normalise both ways (hira→kata + strip dakuten/handakuten) before
// lookup. Otherwise じ wouldn't resolve to シ → 之.
const KANA_RE = /[\u3041-\u3096\u30A1-\u30FA]/;

// Voiced/half-voiced katakana → unvoiced base. Hand-table is faster
// and clearer than Unicode normalisation gymnastics for the ~25 pairs
// we actually care about.
const STRIP_DAKUTEN = {
  ガ:"カ",ギ:"キ",グ:"ク",ゲ:"ケ",ゴ:"コ",
  ザ:"サ",ジ:"シ",ズ:"ス",ゼ:"セ",ゾ:"ソ",
  ダ:"タ",ヂ:"チ",ヅ:"ツ",デ:"テ",ド:"ト",
  バ:"ハ",ビ:"ヒ",ブ:"フ",ベ:"ヘ",ボ:"ホ",
  パ:"ハ",ピ:"ヒ",プ:"フ",ペ:"ヘ",ポ:"ホ",
  ヴ:"ウ",
};

function hiraToKata(ch) {
  const c = ch.charCodeAt(0);
  if (c >= 0x3041 && c <= 0x3096) return String.fromCharCode(c + 0x60);
  return ch;
}

function normaliseForLookup(ch) {
  const k = hiraToKata(ch);
  return STRIP_DAKUTEN[k] || k;
}

/**
 * KanaWithOrigins — splits a string of mixed text + katakana, wrapping
 * each origin-bearing katakana glyph in a tooltip that surfaces its
 * man'yougana etymology (origin kanji, visual link, sound retention,
 * phonetic-family bonus). Tapping the kana navigates to the origin
 * kanji's card.
 *
 * Non-katakana characters render untouched. Katakana characters that
 * don't have a known origin (long mark ー, the n-mark ン only on some
 * older systems, etc.) render as plain text too — never decorate
 * something we can't substantiate.
 */
export default function KanaWithOrigins({ text, className }) {
  const navigate = useNavigate();
  const origins = useKanaOrigins();
  const segments = useMemo(() => splitKatakana(text || ""), [text]);

  return (
    <span className={className}>
      <TooltipProvider delayDuration={120}>
        {segments.map((seg, i) => {
          // Segments come from a deterministic split of `text`, so position
          // is the only thing that uniquely identifies one (the same kana
          // can legitimately appear twice in a reading like しし). Combine
          // index + char for a stable, unique key.
          const segKey = `${i}-${seg.text}`;
          if (!seg.kana) return <span key={segKey}>{seg.text}</span>;
          const lookupKana = normaliseForLookup(seg.text);
          const info = origins?.[lookupKana];
          if (!info) return <span key={segKey}>{seg.text}</span>;
          return (
            <Tooltip key={segKey}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  data-testid={`kana-tooltip-${seg.text}`}
                  onClick={() => navigate(`/kanji/${encodeURIComponent(info.kanji)}`)}
                  className="underline decoration-shu/40 decoration-dotted underline-offset-[3px] hover:decoration-shu hover:text-shu transition-colors cursor-pointer"
                >
                  {seg.text}
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs bg-washi text-sumi border border-borderDefault px-3 py-2 text-left"
              >
                <p className="font-serif text-sm leading-snug">
                  <span className="text-shu text-base">{seg.text}</span> (kana{" "}
                  <span className="font-serif">{lookupKana}</span>) simplified from{" "}
                  <span className="text-shu font-serif text-base">{info.kanji}</span>{" "}
                  ({info.kanji_meaning})
                </p>
                <p className="font-sans text-[11px] text-sumi/80 mt-1.5">
                  visual: {info.visual_link}
                </p>
                <p className="font-sans text-[11px] text-sumi/80 mt-1">
                  {info.sound_retained
                    ? `still reads ${info.sound} → mnemonic alive`
                    : `historical only — modern reading has drifted from ${info.sound}`}
                </p>
                {info.is_phonetic_driver && info.family_size > 0 && (
                  <>
                    {(() => {
                      const sampleCount = Array.isArray(info.family_sample) ? info.family_sample.length : 0;
                      const more = Math.max(0, info.family_size - sampleCount);
                      return (
                        <p
                          className="font-sans text-[11px] text-shu mt-1.5"
                          data-testid={`kana-family-cta-${seg.text}`}
                        >
                          {more > 0
                            ? <>+{more} more · tap <span className="font-serif">{info.kanji}</span> to see the full family</>
                            : <>drives a family of {info.family_size} kanji · tap <span className="font-serif">{info.kanji}</span> to investigate</>}
                        </p>
                      );
                    })()}
                    {Array.isArray(info.family_sample) && info.family_sample.length > 0 && (
                      <div
                        data-testid={`kana-family-sample-${seg.text}`}
                        className="flex flex-wrap gap-1 mt-2"
                      >
                        {info.family_sample.map((m) => (
                          <button
                            key={m.kanji}
                            type="button"
                            data-testid={`kana-family-member-${m.kanji}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/kanji/${encodeURIComponent(m.kanji)}`);
                            }}
                            title={m.meaning + (m.jlpt ? ` · ${m.jlpt}` : "")}
                            className="flex flex-col items-center justify-center w-10 h-12 border border-borderDefault bg-washi hover:border-shu transition-colors"
                          >
                            <span className="font-serif text-lg text-sumi leading-none">{m.kanji}</span>
                            <span className="text-[8px] uppercase tracking-wider text-sumiSoft font-sans mt-0.5 truncate w-full text-center px-0.5">
                              {(m.meaning || "").split(/[\s,/]/)[0]}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </TooltipProvider>
    </span>
  );
}

function splitKatakana(s) {
  // Tokenise into runs of katakana vs non-katakana, each katakana char
  // gets its own segment so the tooltip can target a single glyph.
  const out = [];
  let buf = "";
  for (const ch of s) {
    if (KANA_RE.test(ch)) {
      if (buf) { out.push({ kana: false, text: buf }); buf = ""; }
      out.push({ kana: true, text: ch });
    } else {
      buf += ch;
    }
  }
  if (buf) out.push({ kana: false, text: buf });
  return out;
}

export function useKanaOrigins() {
  const q = useQuery({
    queryKey: ["kana-origins"],
    queryFn: fetchKanaOrigins,
    staleTime: Infinity,        // never changes between deploys
    gcTime: 1000 * 60 * 60 * 6, // keep in memory for hours
  });
  return q.data || {};
}
